import { Order } from '@/types/commerce'
import { getErrorMessage } from '@/types/error'
import { SESSION_ANON_FALLBACK_HEADER, NO_USER_ERROR_CODE } from '@/constants/session'
import { debug } from '@/utils/debug'
import settings from '@/settings/public'

interface ICookieStore {
  getAll(): { name: string; value: string }[]
}

interface IFetchActiveOrdersOptions {
  /** Pass cookies when calling from server (e.g. layout) to forward session; omit for client fetches (browser sends cookies automatically) */
  cookies?: ICookieStore
  /** Server-only: client headers to forward on the loopback fetch (buildSsrForwardHeaders, PRO-946). */
  forwardHeaders?: Record<string, string>
  /**
   * The no-user error (42215) normally resolves to [] — the fail-open for
   * anonymous client calls. Set this when the caller KNOWS the session is
   * logged-in (SSR prefetch after a User profile): 42215 there means the
   * request was answered by an anonymous fallback session (token-refresh
   * race), and resolving [] would dehydrate a poisoned empty list that the
   * client trusts and never refetches. Throwing instead leaves no dehydrated
   * entry, so the client performs its normal single fetch.
   */
  throwOnNoUser?: boolean
}

export const fetchActiveOrders = async (options?: IFetchActiveOrdersOptions): Promise<Order[]> => {
  const url = new URL(`${settings.api.host}/api/1.0/commerce/orders/active`)

  const cookieHeader = options?.cookies
    ? options.cookies.getAll().map(c => `${c.name}=${c.value}`).join('; ')
    : undefined

  const reqHeaders: Record<string, string> = { ...options?.forwardHeaders }
  if (cookieHeader) reqHeaders['Cookie'] = cookieHeader

  try {
    const response = await fetch(url.toString(), { headers: reqHeaders })

    // The backend may answer an anonymous session with 200 + empty items
    // instead of a 42215 error — the code check below never sees those. The
    // session middleware marks degraded (user-token-arrived-but-answered-
    // anonymous) responses with this header; treat them like 42215.
    if (options?.throwOnNoUser && response.headers.get(SESSION_ANON_FALLBACK_HEADER)) {
      throw new Error('Active orders answered by an anonymous fallback session for a logged-in caller (header)')
    }

    const ordersJson = await response.json()

    if (ordersJson.error) {
      if (String(ordersJson.error.code) === NO_USER_ERROR_CODE) {
        if (options?.throwOnNoUser) {
          throw new Error('Active orders answered by an anonymous session for a logged-in caller (42215)')
        }
        return []
      }

      // getErrorMessage, not `.userMessage || .message`: post-PRO-200 the BFF
      // always sets userMessage as a TranslationType OBJECT, which the old
      // truthy-chain stringified to "[object Object]".
      throw new Error(getErrorMessage(ordersJson.error))
    }

    return ordersJson.data.items

  } catch (error: any) {
    debug.error('Error fetching active orders:', error)
    throw new Error(`Active orders fetch failed: ${error.message}`)
  }
}